import React, { useState, useEffect, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { getSrcSet } from "../../utils/media";

const rootStyle = {
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
};

const widgetContainerStyle = {
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "150px",
  borderRadius: "10px 10px 0 0",
};

const previewStyle = {
  opacity: 1,
  filter: "blur(12px)",
  objectFit: "cover",
  objectPosition: "center center",
  position: "absolute",
  width: "100%",
  height: "100%",
  left: 0,
  top: 0,
  display: "inline-block",
};

const fullSizeStyle = {
  opacity: 0,
  objectFit: "cover",
  objectPosition: "center center",
  position: "absolute",
  width: "100%",
  height: "100%",
  left: 0,
  top: 0,
};

const widgetImageStyle = {
  width: "110%",
  height: "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

export default ({
  mediaType,
  mediaKey,
  sizes,
  imagePreview,
  alt = "",
  wrapperStyle,
  imgStyle,
  className,
  noAnimation,
  isWidget,
  eager,
}) => {
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "400px 0px 400px 0px",
    root: null,
    initialInView: eager,
  });

  const [imageLoaded, setImageLoaded] = useState(noAnimation);

  const [imageLoadStartTime, setImageLoadStartTime] = useState();
  const [animation, setAnimation] = useState(
    noAnimation ? { transition: "none" } : undefined,
  );
  useEffect(() => {
    if (inView && !imageLoadStartTime) {
      setImageLoadStartTime(new Date().getTime());
    }
  }, [inView, imageLoadStartTime]);

  useEffect(() => {
    if (!imageLoadStartTime) {
      return;
    }

    if (new Date().getTime() < imageLoadStartTime + 20) {
      setAnimation({ transition: "opacity 0.1s ease 0s" });
    } else {
      setAnimation({ transition: "opacity 0.5s ease 0s" });
    }
  }, [imageLoaded, imageLoadStartTime]);

  const handleImageLoaded = useCallback((e) => {
    setImageLoaded(true);
  }, []);

  const handleImageLoadingFailed = useCallback((e) => {
    console.log("image failed to load, not handled.");
  }, []);

  const srcSet = getSrcSet(mediaType, mediaKey);
  const srcSetFallback = getSrcSet(mediaType, mediaKey, true);

  const fullImageStyle = {
    ...(isWidget ? widgetImageStyle : fullSizeStyle),
    ...imgStyle,
    ...(imageLoaded && animation && { opacity: 1 }),
    ...animation,
  };

  const imageParams = {
    sizes,
    alt,
    style: fullImageStyle,
    onLoad: handleImageLoaded,
    onError: handleImageLoadingFailed,
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...(isWidget ? widgetContainerStyle : rootStyle),
        ...wrapperStyle,
      }}
    >
      {!noAnimation && (
        <img
          src={imagePreview}
          alt={alt}
          style={{
            ...(isWidget ? widgetImageStyle : previewStyle),
            ...imgStyle,
            ...(imageLoaded && animation && { opacity: 0 }),
            ...animation,
          }}
        />
      )}
      {(inView || imageLoaded) && (
        <picture>
          <source srcSet={srcSet} {...imageParams} type="image/webp" />
          <source srcSet={srcSetFallback} {...imageParams} type="image/jpeg" />
          <img
            srcSet={srcSetFallback}
            loading={eager ? "eager" : "lazy"}
            {...imageParams}
          />
        </picture>
      )}
    </div>
  );
};
